import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { BOOKABLE_SERVICES, SITE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate(),
    ).padStart(2, "0")}`;

const prettyDate = (d) =>
    d.toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });

const todayMidnight = () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
};

/**
 * BookingForm — paid session booking.
 * 1) Choose one of the three offerings
 * 2) Share details
 * 3) Pick a weekday date (no past dates) + an IST time slot
 * 4) Razorpay checkout — booking is confirmed only after payment verifies
 */
export default function BookingForm() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [service, setService] = useState("");
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [date, setDate] = useState(undefined);
    const [time, setTime] = useState("");
    const [avail, setAvail] = useState(null);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [loading, setLoading] = useState(false);

    // Preselect a service when arriving from a "Book" CTA (?service=slug)
    useEffect(() => {
        const s = params.get("service");
        if (s && BOOKABLE_SERVICES.some((x) => x.slug === s)) setService(s);
    }, [params]);

    const fetchAvailability = (d) => {
        setSlotsLoading(true);
        axios
            .get(`${API}/bookings/availability`, {
                params: { date: fmtDate(d) },
            })
            .then((r) => setAvail(r.data))
            .catch(() => setAvail(null))
            .finally(() => setSlotsLoading(false));
    };

    const onSelectDate = (d) => {
        setDate(d);
        setTime("");
        if (d) fetchAvailability(d);
    };

    const onChange = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.value }));

    const selected = BOOKABLE_SERVICES.find((s) => s.slug === service);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!service) {
            toast.error("Please choose a session to book.");
            return;
        }
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Please share your name and email.");
            return;
        }
        if (!date || !time) {
            toast.error("Please pick a date and a time slot.");
            return;
        }
        if (typeof window === "undefined" || !window.Razorpay) {
            toast.error(
                "Payment gateway isn't ready. Please refresh and try again.",
            );
            return;
        }

        try {
            setLoading(true);
            const { data } = await axios.post(`${API}/razorpay/order`, {
                program_slug: service,
                customer_name: form.name.trim(),
                customer_email: form.email.trim(),
                customer_phone: form.phone.trim() || undefined,
                booking_date: fmtDate(date),
                booking_time: time,
                message: form.message.trim() || undefined,
            });

            const options = {
                key: data.key_id,
                amount: data.amount,
                currency: data.currency,
                name: "Soul Sync Healing",
                description: `${data.program_name} · ${fmtDate(date)} ${time} IST`,
                order_id: data.order_id,
                prefill: {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    contact: form.phone.trim(),
                },
                notes: { program_slug: service },
                theme: { color: "#A68A6B" },
                handler: async (response) => {
                    try {
                        await axios.post(`${API}/razorpay/verify`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        navigate(
                            `/payment/success?order_id=${response.razorpay_order_id}`,
                        );
                    } catch (err) {
                        toast.error(
                            "Payment received but verification failed. Please contact us with your payment ID.",
                        );
                        navigate(
                            `/payment/success?order_id=${response.razorpay_order_id}&status=verify_failed`,
                        );
                    }
                },
                modal: {
                    ondismiss: () => setLoading(false),
                    escape: true,
                    backdropclose: false,
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", (resp) => {
                toast.error(
                    resp?.error?.description ||
                        "Payment failed. No amount has been deducted — you can simply try again.",
                );
                setLoading(false);
            });
            rzp.open();
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error(
                    "That slot was just booked by someone else. Please pick another time.",
                );
                if (date) fetchAvailability(date);
                setTime("");
            } else {
                toast.error(
                    err?.response?.data?.detail ||
                        "Could not start checkout. Please try again or WhatsApp us.",
                );
            }
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={onSubmit}
            className="space-y-14"
            data-testid="booking-form"
        >
            {/* STEP 1 — Choose session */}
            <div>
                <p className="ssh-eyebrow text-[#75736F]">
                    01 · Choose your session
                </p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {BOOKABLE_SERVICES.map((s) => (
                        <button
                            type="button"
                            key={s.slug}
                            onClick={() => setService(s.slug)}
                            className={`text-left p-6 border transition-colors ${
                                service === s.slug
                                    ? "border-[#A68A6B] bg-[#FAF8F5]"
                                    : "border-[#EFEBE4] bg-white hover:border-[#A68A6B]"
                            }`}
                            data-testid={`booking-service-${s.slug}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <p className="ssh-eyebrow">{s.eyebrow}</p>
                                <span className="font-serif text-xl text-[#2C2B29] whitespace-nowrap">
                                    {s.priceLabel}
                                </span>
                            </div>
                            <h4 className="mt-4 font-serif text-xl text-[#2C2B29] leading-tight">
                                {s.name}
                            </h4>
                            <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-[#8A9A86]">
                                {s.duration}
                            </p>
                            {s.savingsLabel && (
                                <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#A68A6B]">
                                    {s.valueLabel} · {s.savingsLabel}
                                </p>
                            )}
                        </button>
                    ))}
                </div>
                {service === "personalized-8-session" && (
                    <p
                        className="mt-4 text-sm italic text-[#75736F]"
                        data-testid="booking-first-session-note"
                    >
                        You'll pick the date for your first session below — the
                        remaining seven are scheduled together with Shreya.
                    </p>
                )}
            </div>

            {/* STEP 2 — Details */}
            <div>
                <p className="ssh-eyebrow text-[#75736F]">02 · Your details</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <Label
                            htmlFor="b-name"
                            className="ssh-eyebrow text-[#75736F]"
                        >
                            Name
                        </Label>
                        <Input
                            id="b-name"
                            value={form.name}
                            onChange={onChange("name")}
                            placeholder="Your full name"
                            className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                            data-testid="booking-input-name"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label
                            htmlFor="b-email"
                            className="ssh-eyebrow text-[#75736F]"
                        >
                            Email
                        </Label>
                        <Input
                            id="b-email"
                            type="email"
                            value={form.email}
                            onChange={onChange("email")}
                            placeholder="you@example.com"
                            className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                            data-testid="booking-input-email"
                        />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                        <Label
                            htmlFor="b-phone"
                            className="ssh-eyebrow text-[#75736F]"
                        >
                            Phone / WhatsApp
                        </Label>
                        <Input
                            id="b-phone"
                            value={form.phone}
                            onChange={onChange("phone")}
                            placeholder="Optional — for session reminders"
                            className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                            data-testid="booking-input-phone"
                        />
                    </div>
                </div>
                <div className="mt-6 space-y-2">
                    <Label
                        htmlFor="b-message"
                        className="ssh-eyebrow text-[#75736F]"
                    >
                        What's bringing you in? (optional)
                    </Label>
                    <Textarea
                        id="b-message"
                        value={form.message}
                        onChange={onChange("message")}
                        placeholder="Share as little or as much as feels right."
                        className="bg-white border-[#EFEBE4] rounded-none min-h-[120px] focus-visible:ring-[#8A9A86]"
                        data-testid="booking-input-message"
                    />
                </div>
            </div>

            {/* STEP 3 — Date & time */}
            <div>
                <p className="ssh-eyebrow text-[#75736F]">
                    03 · Pick a date & time (IST)
                </p>
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    <div>
                        <div
                            className="inline-block border border-[#EFEBE4] bg-white p-3"
                            data-testid="booking-calendar"
                        >
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={onSelectDate}
                                disabled={[
                                    { before: todayMidnight() },
                                    { dayOfWeek: [0, 6] },
                                ]}
                            />
                        </div>
                        <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[#75736F]">
                            Sessions run Monday – Friday · 10:00 – 19:00 IST
                        </p>
                    </div>
                    <div>
                        {!date ? (
                            <p
                                className="text-sm italic text-[#75736F] leading-relaxed"
                                data-testid="booking-slots-hint"
                            >
                                Choose a date on the calendar to see the
                                available time slots.
                            </p>
                        ) : slotsLoading ? (
                            <p
                                className="inline-flex items-center gap-2 text-sm text-[#75736F]"
                                data-testid="booking-slots-loading"
                            >
                                <Loader2
                                    className="h-4 w-4 animate-spin"
                                    strokeWidth={1.5}
                                />
                                Checking availability…
                            </p>
                        ) : !avail || avail.closed ? (
                            <p
                                className="text-sm italic text-[#75736F]"
                                data-testid="booking-slots-closed"
                            >
                                This day isn't available for sessions. Please
                                choose a weekday.
                            </p>
                        ) : (
                            <div data-testid="booking-slots">
                                <p className="text-sm text-[#2C2B29]">
                                    <span className="font-serif text-lg">
                                        {prettyDate(date)}
                                    </span>
                                </p>
                                <div className="mt-4 grid grid-cols-3 gap-2">
                                    {avail.slots.map((t) => {
                                        const taken = avail.taken.includes(t);
                                        const active = time === t;
                                        return (
                                            <button
                                                type="button"
                                                key={t}
                                                disabled={taken}
                                                onClick={() => setTime(t)}
                                                className={`h-11 text-sm tracking-[0.08em] border transition-colors ${
                                                    taken
                                                        ? "border-[#EFEBE4] text-[#C9C4B8] line-through cursor-not-allowed bg-[#FAF8F5]"
                                                        : active
                                                          ? "border-[#A68A6B] bg-[#A68A6B] text-white"
                                                          : "border-[#EFEBE4] text-[#2C2B29] hover:border-[#A68A6B] bg-white"
                                                }`}
                                                data-testid={`booking-slot-${t.replace(":", "")}`}
                                            >
                                                {t}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[#75736F]">
                                    All times IST · held for you while you pay
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* STEP 4 — Summary & pay */}
            <div className="border-t border-[#EFEBE4] pt-8 flex flex-col lg:flex-row lg:items-center gap-8">
                <div
                    className="flex-1 text-sm leading-relaxed text-[#4A4947]"
                    data-testid="booking-summary"
                >
                    {selected && date && time ? (
                        <>
                            <span className="font-serif text-lg text-[#2C2B29]">
                                {selected.name}
                            </span>
                            <br />
                            {prettyDate(date)} · {time} IST ·{" "}
                            {selected.priceLabel}
                        </>
                    ) : (
                        <span className="italic text-[#75736F]">
                            Choose a session, date and time to continue to
                            secure payment.
                        </span>
                    )}
                </div>
                <Button
                    type="submit"
                    disabled={loading}
                    className="ssh-btn-primary rounded-none px-10 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-3 whitespace-nowrap"
                    data-testid="booking-pay-submit"
                >
                    {loading ? (
                        <>
                            <Loader2
                                className="h-4 w-4 animate-spin"
                                strokeWidth={1.75}
                            />
                            Opening secure checkout…
                        </>
                    ) : (
                        <>
                            <ShieldCheck
                                className="h-4 w-4"
                                strokeWidth={1.5}
                            />
                            Pay {selected ? selected.priceLabel : ""} & Confirm
                        </>
                    )}
                </Button>
            </div>
            <div className="flex flex-col gap-2">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#75736F]">
                    Secure · Razorpay · UPI · Cards · Netbanking · Wallets
                </p>
                <a
                    href={`https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
                        "Hi Shreya, I would like to book a session. Could you help me with the payment?",
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] uppercase tracking-[0.18em] text-[#A68A6B] hover:text-[#897157]"
                >
                    Trouble paying? · Chat with Shreya
                </a>
            </div>
        </form>
    );
}
